import { useCallback, useContext, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { faker } from '@faker-js/faker';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { UserContext } from 'contexts/UserContext';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';
import {
  NEW_ADULT_MEMBER,
  NEW_MINOR_MEMBER,
  FIELDS,
} from './AffordaHousehold.Members.constants';
import APPLICANT_TYPES from 'constants/applicantTypes';
import AFFORDABLE_RELATIONSHIPS from 'constants/affordableRelationships';
import { ADDITIONAL_MINORS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.AdditionalMinors.constants';
import {
  getMemberFieldId,
  getPrimaryMember,
  isMinor as checkIsMinor,
} from 'pages/AffordaHousehold/utils';
import useUserProfileQuery from 'hooks/queries/useUserProfileQuery';
import useValidatePhoneNumber from 'hooks/useValidatePhoneNumber';
import useValidateEmail from 'hooks/useValidateEmail';
import useReactHookFormProps from 'hooks/useReactHookFormProps';
import { getIsDevelopment } from 'constants/environment';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import { checkCanInviteOthers } from 'pages/AffordaHousehold/utils';
import useAffordableRelationshipsMap from 'hooks/useAffordableRelationshipsMap';

export const useMemberCount = (members) => {
  return useMemo(() => {
    let minorsCount = 0;
    let adultsCount = 0;

    members?.forEach((member) => {
      if (member.applicantType === APPLICANT_TYPES.MINOR) {
        minorsCount++;
      } else {
        adultsCount++;
      }
    });

    return {
      minorsCount,
      adultsCount,
      hasMinors: minorsCount > 0,
      has2Adults: adultsCount === 2,
      hasMoreThanOneAdult: adultsCount > 1,
    };
  }, [members]);
};

const getSumOfAdditionalMinors = (additionalMinors) => {
  return (
    (additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.pregnancyCount] ?? 0) +
    (additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.adoptionCount] ?? 0) +
    (additionalMinors?.[ADDITIONAL_MINORS_FIELD_NAMES.guardianshipCount] ?? 0)
  );
};

const getDefaultValues = (members = []) => {
  return members.reduce((object, member) => {
    object[member.id] = {
      id: member.id,
      firstName: member.firstName ?? '',
      lastName: member.lastName ?? '',
      emailAddress: member.emailAddress ?? '',
      phoneNumber: !!member?.phoneNumber?.length
        ? member?.phoneNumber
        : getIsDevelopment()
          ? '9099999999'
          : '',
      birthday: member.birthday
        ? moment(member.birthday)
        : getIsDevelopment()
          ? moment('01-01-1990')
          : null,
    };

    return object;
  }, {});
};

const useAffordaHouseholdMembers = ({
  hasCompletedSummary,
  householdForm: { members, additionalMinors },
  setHouseholdForm,
  handleUpdateMember,
}) => {
  const intl = useIntl();
  const {
    userProfile: { firstName, lastName, phoneNumber },
    authUser: { prospect, customer },
  } = useContext(UserContext);
  const { data: { householdId } = {}, isFetched } = useUserProfileQuery();
  const { householdMembers } = useHouseholdMembersContext();
  const hasHouseholdMembersInDB = Boolean(householdMembers?.length);

  const emailAddress = prospect?.emailAddress ?? customer?.emailAddress;

  const defaultValues = useMemo(() => getDefaultValues(members), [members]);

  const affordableRelationshipsMap = useAffordableRelationshipsMap();

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onChange',
  });

  const { control, getValues, isValid, reset } = ReactHookFormProps;

  // Add this effect to reset the form when defaultValues change
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!isFetched) return;
    // if householdForm is not initialized, set it

    const shouldInitializeMembers = !members?.length;

    if (shouldInitializeMembers) {
      const initialMembers = [
        // add primary member / portal account holder
        {
          ...NEW_ADULT_MEMBER,
          isPrimary: true,
          id: uuid(),
          applicantType: APPLICANT_TYPES.FINANCIAL_LEASE_SIGNER,
          householdDesignation: HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD,
          affordableRelationship: AFFORDABLE_RELATIONSHIPS.HEAD_OF_HOUSEHOLD,
          firstName,
          lastName,
          emailAddress,
          phoneNumber,
          isValid: false,
        },
      ];
      setHouseholdForm((prevState) => ({
        ...prevState,
        members: initialMembers,
      }));

      reset(getDefaultValues(initialMembers));
    }
  }, [
    emailAddress,
    isFetched,
    firstName,
    lastName,
    phoneNumber,
    setHouseholdForm,
    householdId?.length,
    members?.length,
    reset,
  ]);

  // if there is only one member in the household,
  // set them as the head of household
  useEffect(() => {
    if (
      members?.length === 1 &&
      members[0]?.householdDesignation !==
        HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD
    ) {
      setHouseholdForm((prevState) => {
        const members = prevState.members.map((member) => ({
          ...member,
          applicantType: APPLICANT_TYPES.FINANCIAL_LEASE_SIGNER,
          householdDesignation: HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD,
          affordableRelationship: AFFORDABLE_RELATIONSHIPS.HEAD_OF_HOUSEHOLD,
        }));
        return { ...prevState, members };
      });
    }
  }, [members, setHouseholdForm]);

  const handleAddMember = useCallback(
    (applicantType) => {
      const isMinor = checkIsMinor({ applicantType });
      const newMember = isMinor ? NEW_MINOR_MEMBER : NEW_ADULT_MEMBER;
      setHouseholdForm((prevState) => {
        const values = getValues();
        const updatedMembers =
          prevState?.members.map((member) => ({
            ...member,
            ...(values?.[member?.id] ?? {}),
            isOpen: false,
          })) ?? [];

        updatedMembers.push({
          ...newMember,
          id: uuid(),
          applicantType,
          ...(getIsDevelopment() && {
            firstName: faker.name.firstName(),
            lastName: faker.name.lastName(),
            birthday: isMinor ? '01-01-2016' : '01-01-1990',
            ...(!isMinor && {
              emailAddress: faker.internet.email(),
              phoneNumber: '9099999999',
            }),
          }),
        });
        return { ...prevState, members: updatedMembers };
      });
    },
    [getValues, setHouseholdForm],
  );

  const {
    hasMinors,
    minorsCount,
    adultsCount,
    has2Adults,
    hasMoreThanOneAdult,
  } = useMemberCount(members);

  const validateEmail = useValidateEmail();
  const validatePhoneNumber = useValidatePhoneNumber();

  const fields = useMemo(() => {
    return members?.reduce((object, member) => {
      const isMinor = checkIsMinor(member);
      object[member.id] = FIELDS({
        id: getMemberFieldId(member.id),
        memberId: member.id,
        isMinor,
        intl,
        validateEmail,
        validatePhoneNumber,
      });

      return object;
    }, {});
  }, [members, intl, validateEmail, validatePhoneNumber]);

  const handleSubmit = useCallback(() => {
    const values = getValues();
    setHouseholdForm(
      (prevState) => {
        const newMembers = prevState.members.map((prevMember) => {
          const memberUpdates = values[prevMember.id];
          return {
            ...prevMember,
            ...memberUpdates,
            birthday: formatDateDB(
              memberUpdates?.birthday ?? prevMember.birthday,
            ),
          };
        });

        return { ...prevState, members: newMembers };
      },
      { control, fields },
    );
  }, [control, fields, getValues, setHouseholdForm]);

  const additionalMinorsCount = useMemo(() => {
    if (hasHouseholdMembersInDB && hasCompletedSummary) {
      return getSumOfAdditionalMinors(
        getPrimaryMember(householdMembers)?.additionalMinors,
      );
    }

    if (
      additionalMinors?.[
        ADDITIONAL_MINORS_FIELD_NAMES.expectingMinorsType
      ].includes(ADDITIONAL_MINORS_FIELD_NAMES.notApplicable)
    )
      return 0;

    return additionalMinors?.[
      ADDITIONAL_MINORS_FIELD_NAMES.expectingMinorsType
    ].some((minorType) =>
      [
        ADDITIONAL_MINORS_FIELD_NAMES.pregnancy,
        ADDITIONAL_MINORS_FIELD_NAMES.adoption,
        ADDITIONAL_MINORS_FIELD_NAMES.guardianship,
      ].includes(minorType),
    )
      ? getSumOfAdditionalMinors(additionalMinors)
      : 0;
  }, [
    additionalMinors,
    hasHouseholdMembersInDB,
    hasCompletedSummary,
    householdMembers,
  ]);

  const canInviteOthers = useMemo(() => {
    return checkCanInviteOthers(members, affordableRelationshipsMap);
  }, [members, affordableRelationshipsMap]);

  return {
    additionalMinorsCount,
    hasHouseholdMembersInDB,
    submittedHouseholdMembers: householdMembers,
    ReactHookFormProps,
    fields,
    members: members ?? [],
    handleUpdateMember,
    handleSubmit,
    handleAddMember,
    isValid,
    adultsCount,
    hasMinors,
    minorsCount,
    has2Adults,
    hasMoreThanOneAdult,
    canInviteOthers,
  };
};

export default useAffordaHouseholdMembers;
